import React, { useState } from "react";
import { Box, Modal, TextField, Alert, Checkbox, FormControlLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { setLogDoc } from "@/firebase/logs";
import { saveEmail } from "@/firebase/emails";
import { selectUserState } from "@/store/slices/user.slice";
import { useAppSelector } from "@/hooks/common";
import { StyledButton } from "@/components/button";
import { currentTimeInSeconds } from "@/utils/common";
import { AppRoutes } from "@/constants/app.routes";
import { subscribeToKlaviyo } from "@/utils/klaviyo";

import { ModalBody, ModalContent, ModalTitle } from "./shared";

interface Props {
  open: boolean;
  onClose: () => void;
}

// This modal is shown when the rating is above 3 stars
export const ModalAbove3: React.FC<Props> = ({ open, onClose }) => {
  const user = useAppSelector(selectUserState).user!;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(true); // Default checked
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const submitToKlaviyo = async (email: string) => {
    try {
      const success = await subscribeToKlaviyo(email);
      
      if (success) {
        console.log('Successfully submitted to Klaviyo');
      } else {
        console.error('Failed to submit to Klaviyo');
      }
      
      return success;
    } catch (error) {
      console.error('Error submitting to Klaviyo:', error);
      return false;
    }
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setErrorMessage("Bitte geben Sie eine E-Mail-Adresse ein.");
      return;
    }

    if (!isValidEmail(email)) {
      setErrorMessage("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    if (!marketingConsent) {
      setErrorMessage("Bitte bestätigen Sie, dass Sie Marketing-E-Mails erhalten möchten.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Submit to Klaviyo first if marketing consent is given
      let klaviyoSubmitted = false;
      if (marketingConsent) {
        klaviyoSubmitted = await submitToKlaviyo(email.trim());
      }

      // Save email to emails collection
      await saveEmail({
        email: email.trim(),
        phone_number: user.phone_number,
        timestamp: currentTimeInSeconds(),
        source: "rating_feedback",
        marketing_consent: marketingConsent,
        klaviyo_submitted: klaviyoSubmitted,
      });

      // Log the email submission
      await setLogDoc(user.phone_number, {
        type: "email_submitted",
        timestamp: currentTimeInSeconds(),
        email: email.trim(),
      });

      setShowSuccessMessage(true);
      setEmail("");
      
      // Navigate to difficulty page after a short delay
      setTimeout(() => {
        onClose();
        setShowSuccessMessage(false);
        navigate(AppRoutes.select_difficulty);
      }, 2000);

    } catch (error) {
      console.error("Error submitting email:", error);
      setErrorMessage("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
    navigate(AppRoutes.select_difficulty);
  };

  return (
    <div>
      <Modal open={open} onClose={onClose}>
        <ModalContent>
          <Box mb={2}>
            <ModalTitle title={"VIELEN DANK!"} titleColor={"green"} />
          </Box>

          {showSuccessMessage ? (
            <Box mb={2}>
              <Alert severity="success">
                Vielen Dank! Ihre E-Mail wurde erfolgreich gespeichert. Sie werden zur Schwierigkeitsauswahl weitergeleitet...
              </Alert>
            </Box>
          ) : (
            <>
              <Box mb={2}>
                <ModalBody
                  text={
                    "Möchten Sie über neue Crime Cases informiert werden? Hinterlassen Sie uns Ihre E-Mail-Adresse und wir halten Sie auf dem Laufenden!"
                  }
                />
              </Box>

              <Box mb={2}>
                <TextField
                  fullWidth
                  type="email"
                  label="E-Mail-Adresse"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!errorMessage}
                  helperText={errorMessage}
                  disabled={isSubmitting}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-input': {
                      fontSize: '1.1rem',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '1.1rem',
                    },
                  }}
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      disabled={isSubmitting}
                    />
                  }
                  label="Marketing Mails erhalten (optional)"
                  sx={{ 
                    alignItems: 'center',
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '1.5rem',
                      lineHeight: 1.43,
                      marginLeft: '8px',
                    },
                    '& .MuiCheckbox-root': {
                      padding: '9px',
                    }
                  }}
                />
              </Box>

              <Box mb={1}>
                <StyledButton
                  onClick={handleEmailSubmit}
                  disabled={isSubmitting || !marketingConsent || !email.trim()}
                  variant="contained"
                  fullWidth
                >
                  {isSubmitting ? "Wird gesendet..." : "E-Mail speichern"}
                </StyledButton>
              </Box>

              <Box>
                <StyledButton
                  onClick={handleSkip}
                  variant="text"
                  fullWidth
                  disabled={isSubmitting}
                >
                  Überspringen
                </StyledButton>
              </Box>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};
